import type { RootState } from 'src/redux/store';
import type { BoxProps } from '@mui/material/Box';

import { useState } from 'react';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Popover from '@mui/material/Popover';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import CardContent from '@mui/material/CardContent';

import { useTranslate } from 'src/locales';
import { useAppSelector } from 'src/hook/hooks';
import CardDetailsDialog from 'src/pages/setting/components/billing/shared/dialogs/payment-dialog';
import { AddBalanceDialog } from 'src/pages/setting/components/billing/shared/dialogs/add-balance-dialog';

import { Iconify } from 'src/components/iconify';

import { formatSARCurrency } from '../utils';
import { AddFundsForm } from './add-funds-form';
import { countUp, balanceUpdateGlow } from '../animations';
import { LoadingText, PulseAnimation, ShakeAnimation } from './shared-components';
import { CurrencySymbol } from '../../../../pages/setting/components/billing/utils/currency-utils';

// ----------------------------------------------------------------------

type BalanceInfo = {
  main: number;
  accountant: number;
  available: number;
  isLow: boolean;
  isCritical: boolean;
};

interface BalanceCompactProps extends BoxProps {
  manager: any;
  balance: BalanceInfo;
  isLoading: boolean;
  isSuperAdmin: boolean;
  noActivePlan: boolean;
  balanceUpdateAnimation: boolean;
}

export function BalanceCompact({
  manager,
  balance,
  isLoading,
  isSuperAdmin,
  noActivePlan,
  balanceUpdateAnimation,
  sx,
  ...other
}: BalanceCompactProps) {
  const theme = useTheme();
  const { t } = useTranslate('translation');

  const {
    addFundsAmount,
    setAddFundsAmount,
    isProcessingFunds,
    showAddFundsForm,
    setShowAddFundsForm,
    showSuccess,
    animateAmount,
    clickedButton,
    showMoneyEffect,
    estimateItem,
    isChargeEstimateLoading,
    cardDialog,
    cardPaymentDialog,
    handleAddFunds,
    handleQuickAmountClick,
    onConfirmaHandler,
    handlePaymentClose,
  } = manager;

  const { main, accountant, available, isLow, isCritical } = balance;
  const userRole = useAppSelector((state: RootState) => {
    const data = state.auth.loginApiData;
    return data && 'role' in data ? (data as any).role : null;
  });
  const subscriptionCurrency = useAppSelector((state: RootState) => {
    const billing = (state as any).billing;
    return (
      billing?.activeSubscription?.plan?.currency ||
      billing?.activeSubscription?.plan?.currency_code ||
      'SAR'
    );
  });
  /** Parent `BalanceDisplay` already hides this widget without billing (or super-admin). */
  const canAddBalance = !userRole || userRole.toLowerCase() !== 'user';

  const [balanceAnchorEl, setBalanceAnchorEl] = useState<HTMLElement | null>(null);
  const balancePopoverOpen = Boolean(balanceAnchorEl);

  const handleBalanceClick = (event: React.MouseEvent<HTMLElement>) => {
    setBalanceAnchorEl(event.currentTarget);
  };

  const handleBalanceClose = () => {
    setBalanceAnchorEl(null);
    setAddFundsAmount('');
    setShowAddFundsForm(false);
  };

  return (
    <>
      {canAddBalance && (
        <Box
          onClick={handleBalanceClick}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2,
            py: 1,
            borderRadius: 1.5,
            transition: 'all 0.2s ease-in-out',
            cursor: 'pointer',
            ...sx,
          }}
          {...other}
        >
          <ShakeAnimation criticalBalance={isCritical}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 36,
                height: 36,
                borderRadius: '50%',
                backgroundColor:
                  available > 0
                    ? isLow
                      ? theme.palette.warning.lighter
                      : theme.palette.success.lighter
                    : theme.palette.error.lighter,
                transition: 'all 0.3s ease',
                transform: balancePopoverOpen ? 'rotate(10deg)' : 'rotate(0deg)',
              }}
            >
              <Iconify
                icon="mdi:wallet"
                width={20}
                height={20}
                sx={{
                  color:
                    available > 0
                      ? isLow
                        ? theme.palette.warning.main
                        : theme.palette.success.main
                      : theme.palette.error.main,
                }}
              />
            </Box>
          </ShakeAnimation>
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', lineHeight: 1 }}
            >
              {t('AVAILABLE_BALANCE')}
            </Typography>
            <PulseAnimation loading={isLoading} lowBalance={isLow}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  lineHeight: 1.2,
                  color: available > 0 ? (isLow ? 'warning.main' : 'success.main') : 'error.main',
                  animation:
                    balanceUpdateAnimation && !balancePopoverOpen
                      ? `${balanceUpdateGlow} 2s ease-in-out, ${countUp} 0.5s ease-out`
                      : 'none',
                  transition: 'all 0.3s ease-in-out',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {isLoading ? (
                  <LoadingText text="Loading..." />
                ) : (
                  <>
                    <CurrencySymbol currency={subscriptionCurrency} sx={{ mr: 0.5 }} />{' '}
                    {formatSARCurrency(available)}
                  </>
                )}
              </Typography>
            </PulseAnimation>
          </Box>
          <Iconify
            icon="eva:chevron-down-fill"
            width={16}
            height={16}
            sx={{
              cursor: 'pointer',
              color: 'text.secondary',
              transition: 'transform 0.2s ease-in-out',
              transform: balancePopoverOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          />
        </Box>
      )}

      <Popover
        open={balancePopoverOpen}
        anchorEl={balanceAnchorEl}
        onClose={handleBalanceClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        slotProps={{
          paper: {
            sx: {
              mt: 1.5,
              ml: 0.75,
              width: 320,
              maxHeight: 275,
              overflowY: 'auto',
              boxShadow: theme.customShadows?.dropdown || theme.shadows[8],
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              transformOrigin: 'top right',
              animation: balancePopoverOpen ? 'scaleIn 0.2s cubic-bezier(0.4, 0, 0.2, 1)' : 'none',
              '@keyframes scaleIn': {
                '0%': { opacity: 0, transform: 'scale(0.95)' },
                '100%': { opacity: 1, transform: 'scale(1)' },
              },
            },
          },
        }}
      >
        <Card sx={{ boxShadow: 'none', bgcolor: 'transparent' }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2.5, fontWeight: 600 }}>
              {t('BALANCE_OVERVIEW')}
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Available Balance */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      backgroundColor:
                        available > 0
                          ? isLow
                            ? theme.palette.warning.main
                            : theme.palette.success.main
                          : theme.palette.error.main,
                      animation: `${balanceUpdateGlow} 2s infinite`,
                    }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {t('AVAILABLE_BALANCE')}
                  </Typography>
                </Box>
                <PulseAnimation loading={isLoading} lowBalance={false}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color:
                        available > 0 ? (isLow ? 'warning.main' : 'success.main') : 'error.main',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    {isLoading ? (
                      <LoadingText text="..." />
                    ) : (
                      <>
                        <CurrencySymbol currency={subscriptionCurrency} sx={{ mr: 0.5 }} />{' '}
                        {formatSARCurrency(available)}
                      </>
                    )}
                  </Typography>
                </PulseAnimation>
              </Box>

              {/* Main Balance */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      backgroundColor: theme.palette.info.main,
                    }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {t('MAIN_BALANCE')}
                  </Typography>
                </Box>
                <PulseAnimation loading={isLoading} lowBalance={false}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: main > 0 ? 'info.main' : 'text.secondary',
                      animation: balanceUpdateAnimation
                        ? `${balanceUpdateGlow} 2s ease-in-out, ${countUp} 0.5s ease-out`
                        : 'none',
                      transition: 'all 0.3s ease-in-out',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    {isLoading ? (
                      <LoadingText text="..." />
                    ) : (
                      <>
                        <CurrencySymbol currency={subscriptionCurrency} sx={{ mr: 0.5 }} />{' '}
                        {formatSARCurrency(main)}
                      </>
                    )}
                  </Typography>
                </PulseAnimation>
              </Box>

              {/* Accountant Balance */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      backgroundColor: theme.palette.primary.main,
                    }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {t('ACCOUNTANT_BALANCE')}
                  </Typography>
                </Box>
                <PulseAnimation loading={isLoading} lowBalance={false}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: accountant > 0 ? 'primary.main' : 'text.secondary',
                      animation: balanceUpdateAnimation
                        ? `${balanceUpdateGlow} 2s ease-in-out, ${countUp} 0.5s ease-out`
                        : 'none',
                      transition: 'all 0.3s ease-in-out',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    {isLoading ? (
                      <LoadingText text="..." />
                    ) : (
                      <>
                        <CurrencySymbol currency={subscriptionCurrency} sx={{ mr: 0.5 }} />{' '}
                        {formatSARCurrency(accountant)}
                      </>
                    )}
                  </Typography>
                </PulseAnimation>
              </Box>
            </Box>

            {canAddBalance && (
              <AddFundsForm
                open={showAddFundsForm}
                onToggle={() => setShowAddFundsForm((prev: any) => !prev)}
                amount={addFundsAmount}
                onAmountChange={setAddFundsAmount}
                onAddFunds={() => handleAddFunds()}
                isProcessing={isProcessingFunds}
                showSuccess={showSuccess}
                showMoneyEffect={showMoneyEffect}
                animateAmount={animateAmount}
                clickedButton={clickedButton}
                onQuickAmountClick={handleQuickAmountClick}
                noActivePlan={noActivePlan}
                t={t}
              />
            )}
          </CardContent>
        </Card>
      </Popover>

      {canAddBalance && (
        <>
          <AddBalanceDialog
            isOpen={cardDialog.value}
            onCloseHandler={cardDialog.onFalse}
            onConfirmHandler={() => onConfirmaHandler(estimateItem)}
            t={t}
            isLoading={isChargeEstimateLoading}
            estimateItem={estimateItem}
            isSuperAdmin={isSuperAdmin}
          />

          {estimateItem && (
            <CardDetailsDialog
              open={cardPaymentDialog.value}
              onClose={handlePaymentClose}
              estimateItem={estimateItem}
              cardDetailsKey="addBalance"
              actionPayload={{ topUp: estimateItem?.sub_total || 0 }}
              isSuperAdmin={isSuperAdmin}
              currency={estimateItem?.currency_code || 'SAR'}
            />
          )}
        </>
      )}
    </>
  );
}
